import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { CENTERS } from '@/common/types/centers';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { filterBookableMappedCenterServices, mapDoctorCenterServices } from '@/modules/booking/functions/mapDoctorCenterServices';
import { enrichProfileCentersWithSamanAvailability } from '@/modules/booking/functions/mapSamanAvailabilityToCenters';
import { buildDoctorProfileDocumentTitle } from '@/modules/profile/utils/buildDoctorProfileDocumentTitle';
import { resolveProfileDisplayName } from '@/modules/profile/utils/resolveProfileDisplayName';
import { formatDoctorCitiesForTitle } from './getDoctorCities';
import {
  ProfilePieces,
  buildOverwriteData,
  resolveEffectiveUserData,
  resolveExpertisesWithGroups,
  runOverwrite,
} from './profilePieces';

export type AggregatorOptions = {
  useNewDoctorFullNameAPI?: boolean;
  useNewDoctorExpertiseAPI?: boolean;
  useNewDoctorImageAPI?: boolean;
  useNewDoctorBiographyAPI?: boolean;
  useNewDoctorCentersAPI?: boolean;
  useNewDoctorGalleryAPI?: boolean;
  prefetchSamanAvailability?: boolean;
  /** Explicit SSR decision: true = microservice, false = legacy, undefined = resolve (CSR). */
  useProfileMicroservice?: boolean;
  fetchExpertiseKeywordTags?: boolean;
};

export type AssembleContext = {
  university?: string;
  isServer: boolean;
  options?: AggregatorOptions;
  queryClient?: QueryClient;
};

const createBreadcrumb = (links: { orginalLink: string; title: string }[], displayName: string, currentPathName: string) => {
  const reformattedBreadcrumb = links?.map(link => ({ href: link.orginalLink, text: link.title })) ?? [];
  reformattedBreadcrumb.unshift({ href: '/', text: 'پذیرش۲۴' });
  reformattedBreadcrumb.push({ href: currentPathName, text: displayName });
  return reformattedBreadcrumb;
};

/**
 * Pure assembly of the legacy profile props from fetched pieces.
 * `undefined` piece = pending (its part of the props is `undefined`); `null` = failed/skipped
 * (same empty value the aggregator always used).
 */
export function assembleProfile(pieces: ProfilePieces, ctx: AssembleContext): { props: Record<string, any> } {
  const { university, isServer, options, queryClient } = ctx;
  const has = <K extends keyof ProfilePieces>(k: K) => pieces[k] !== undefined;
  const validatedSlug = pieces.validatedSlug as string;
  const slugInfo = pieces.slugInfo ?? null;
  const pageSlug = `/dr/${validatedSlug}`;

  const overwriteData = buildOverwriteData(pieces);
  if (typeof pieces.pageView === 'number') {
    overwriteData.history = { ...(overwriteData.history ?? {}), count_of_page_view: String(pieces.pageView) };
  }

  const {
    centers: initialCenters,
    expertises,
    feedbacks,
    history,
    information: rawInformation,
    media,
    onlineVisit: rawOnlineVisit,
    similarLinks: rawSimilarLinks,
    symptomes,
    waitingTimeInfo,
  } = runOverwrite(pieces, overwriteData);

  const centerFallbackName = initialCenters?.[0]?.name;
  const displayName = resolveProfileDisplayName(
    { name: rawInformation?.name, family: rawInformation?.family },
    { centerName: centerFallbackName, slug: validatedSlug },
  );
  const information = {
    ...rawInformation,
    ...(displayName
      ? {
          display_name: displayName,
          name: rawInformation?.name?.trim() ? rawInformation.name : displayName.split(' ')[0] ?? displayName,
          family: rawInformation?.family?.trim()
            ? rawInformation.family
            : displayName.includes(' ')
              ? displayName.slice(displayName.indexOf(' ') + 1)
              : '',
        }
      : {}),
  };

  // Hydrate the consult (5532) center from center services — full-profile service ids can be stale.
  let centers = initialCenters;
  const consultCenter = centers?.find?.((center: any) => String(center?.id) === CENTERS.CONSULT);
  let onlineChannels: string[] = [];
  if (consultCenter) {
    const services = pieces.onlineServices;
    if (services?.length) {
      const mapped = filterBookableMappedCenterServices(mapDoctorCenterServices(services as any, consultCenter.user_center_id));
      centers = centers.map((center: any) => (String(center?.id) === CENTERS.CONSULT ? { ...center, services: mapped } : center));
      if (isServer && queryClient) {
        queryClient.setQueryData([ServerStateKeysEnum.DoctorCenterServices, validatedSlug, CENTERS.CONSULT], services);
      }
    }
    onlineChannels = pieces.onlineChannels ?? [];
  }

  const hydratedConsultCenter = centers?.find?.((center: any) => String(center?.id) === CENTERS.CONSULT);
  const onlineVisit = {
    ...rawOnlineVisit,
    enabled: !!hydratedConsultCenter,
    channels: onlineChannels,
  };

  const { expertisesWithGroups } = resolveExpertisesWithGroups(pieces, expertises);

  // Prefer internal-links based items for "تخصص های مرتبط" when available
  let similarLinks = rawSimilarLinks;
  if (Array.isArray(pieces.internalLinks)) {
    const enrichedLinks = pieces.internalLinks.map((item: any) => ({
      caption: item.title,
      link: item.link || item.orginalLink,
    }));
    if (enrichedLinks.length > 0) similarLinks = enrichedLinks;
  }

  const userData = resolveEffectiveUserData(pieces);
  const widgets = pieces.widgets?.widgets ?? [];
  const widgetsData = pieces.widgets?.widgetsData ?? {};

  centers = enrichProfileCentersWithSamanAvailability(centers, pieces.samanAvailability ?? null);

  const doctorCitiesLabel = formatDoctorCitiesForTitle(centers);
  const title = buildDoctorProfileDocumentTitle({
    displayName: information?.display_name,
    expertiseTitle: expertisesWithGroups?.expertises?.[0]?.alias_title,
    doctorCitiesLabel,
  });
  const description = `نوبت دهی اینترنتی ${information?.display_name}، آدرس مطب، شماره تلفن و اطلاعات تماس با امکان رزرو وقت و نوبت دهی آنلاین در اپلیکیشن و سایت پذیرش۲۴`;

  const feedbacksReady =
    has('rateDetails') && (has('reviews') || !!pieces.rateDetails?.hide_rates || pieces.rateDetails === null);
  const reviewsValue = pieces.reviews === undefined ? {} : pieces.reviews;
  const onlineVisitReady = has('centers') && (!consultCenter || has('onlineServices'));
  const keywordTags = pieces.keywordTags;

  return {
    props: {
      title: university ? information?.display_name : title,
      description: university ? '' : description,
      information: {
        ...information,
        user_id: userData?.user_id ?? information?.user_id ?? slugInfo?.user_id ?? null,
      },
      centers: has('centers') ? centers : undefined,
      expertises: expertisesWithGroups,
      feedbacks: feedbacksReady ? { ...feedbacks, feedbacks: reviewsValue } : undefined,
      media: media ? { ...media, gallery: has('gallery') ? media.gallery : undefined } : media,
      symptomes,
      history,
      onlineVisit: onlineVisitReady ? onlineVisit : undefined,
      similarLinks: has('internalLinks') ? similarLinks ?? [] : undefined,
      waitingTimeInfo: has('waitingTime') ? waitingTimeInfo : undefined,
      isBulk: !!(
        centers?.every?.((c: any) => c.status === 2) || centers?.every?.((c: any) => c.services?.every?.((s: any) => !s.hours_of_work))
      ),
      breadcrumbs: createBreadcrumb([], information?.display_name, pageSlug),
      slug: validatedSlug,
      fragmentComponents: {
        // Note: Flags would need to be handled differently on the client
        raviComponentTopOrderProfile: false, // Default value for client
        risman: pieces.risman ?? null,
      },
      hamdastWidgets: has('widgets') ? widgets : undefined,
      hamdastWidgetsData: has('widgets') ? widgetsData : undefined,
      bikoInsurance: has('bikoInsurance') ? pieces.bikoInsurance : undefined,
      centerFacilities: has('centerFacilities') ? pieces.centerFacilities : undefined,
      keywordTags: Array.isArray(keywordTags) ? keywordTags : [],
      expertiseChipsLikeSearchEnabled: !!options?.fetchExpertiseKeywordTags,
      user_id: userData?.user_id ?? information?.user_id ?? slugInfo?.user_id ?? null,
      shouldFetchOnClient: false,
      useSamanAvailabilityStatus: true,
      profile_backend: 'legacy',
      ...(isServer && queryClient ? { dehydratedState: dehydrate(queryClient) } : {}),
    },
  };
}
